import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import { Search, RotateCcw, RefreshCw } from 'lucide-react';
import AffiliateLinksClicks from './AffiliateLinksClicks';
import { getAllAffiliateUsers } from '../../Services/AffiliateInfo/AffiliateApi';
import GlobalPagination from '../../Components/Common/Globalpagination';

// Status and Filter Configurations
const STATUS_STYLES = {
    active: { bg: '#E8F5E8', color: '#2E7D32', border: '1px solid #C8E6C9' },
    pending: { bg: '#FFFBEB', color: '#D97706', border: '1px solid #FDE68A' },
    inactive: { bg: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0' },
    suspended: { bg: '#FFFBEB', color: '#D97706', border: '1px solid #FDE68A' },
    deleted: { bg: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' }
};

const DEFAULT_STATUS_STYLE = { bg: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0' };

const getStatusStyle = (status) => STATUS_STYLES[(status || '').toLowerCase()] || DEFAULT_STATUS_STYLE;

const STATUS_LABELS = {
    active: 'Active',
    suspended: 'Suspended'
};

const PAYABLE_LABELS = {
    above2000: 'Payable now (≥ ₹2,000)',
    below2000: 'Below threshold (< ₹2,000)'
};

const selectMenuProps = {
    PaperProps: {
        sx: {
            boxShadow: 'none',
            border: '1px solid #E2E8F0',
            borderRadius: '6px',
            mt: 0.5,
            p: 0,
            overflow: 'hidden',
            '& .MuiList-root': {
                py: 0.5,
            },
            '& .MuiMenuItem-root': {
                fontSize: '0.82rem',
                fontWeight: 500,
                py: 0.9,
                px: 1.5,
                color: '#1E293B',
                '&.Mui-selected': {
                    bgcolor: '#FFFDF9',
                    color: '#D97706',
                    fontWeight: 600,
                    '&:hover': {
                        bgcolor: '#FFF8EC'
                    }
                },
                '&:hover': {
                    bgcolor: '#F8FAFC'
                }
            }
        }
    }
};

// Format date helper
const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

// Format currency helper
const formatCurrency = (amount) => {
    const num = Number(amount) || 0;
    return `₹${num.toLocaleString('en-IN')}`;
};

const AffiliateInfo = () => {
    // Data states
    const [affiliates, setAffiliates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [payableFilter, setPayableFilter] = useState('');

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [pagination, setPagination] = useState({
        totalItems: 0,
        totalPages: 1,
        currentPage: 1,
        limit: 10,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [apiSummary, setApiSummary] = useState(null);

    // View state
    const [currentView, setCurrentView] = useState('list');
    const [selectedAffiliateForLinks, setSelectedAffiliateForLinks] = useState(null);

    // Map raw API item to UI model
    const getAffiliateData = (item) => {
        if (!item) return null;

        const id = item.affiliateUserId || item.id || item._id;
        const name = item.name || item.affiliateName || item.fullName || '—';
        const email = item.email || '—';
        const joinedDate = item.createdAt || item.joinedDate || null;
        const links = Number(item.totalLinks ?? item.linksCount ?? (Array.isArray(item.links) ? item.links.length : 0));
        const clicks = Number(item.totalClicks ?? item.clicks ?? item.clickCount ?? 0);
        const bookings = Number(item.totalBookings ?? item.bookings ?? item.bookingCount ?? 0);
        const pendingAmount = Number(item.pendingEarnings ?? item.pendingAmount ?? 0);
        const payableAmount = Number(item.payableEarnings ?? item.payableAmount ?? 0);
        const lifetimePaid = Number(item.lifetimePaid ?? 0);
        const status = (item.status || 'active').toLowerCase();

        return {
            id,
            affiliateUserId: id,
            name,
            email,
            joinedDate,
            links,
            clicks,
            bookings,
            pendingAmount,
            payableAmount,
            lifetimePaid,
            status,
            raw: item
        };
    };

    // Fetch affiliates directly from Backend API
    const fetchAffiliates = useCallback(async (currentPage = 1, currentLimit = limit, search = '', status = '', payable = '') => {
        setLoading(true);
        setError(null);
        try {
            const params = { page: currentPage };
            if (currentLimit !== 10) {
                params.limit = currentLimit;
            }
            if (search && search.trim()) params.search = search.trim();
            if (status) params.status = status;
            if (payable) params.payableBalance = payable;

            const response = await getAllAffiliateUsers(params);
            const dataList = response?.affiliates || response?.data || [];
            const paginationObj = response?.pagination || {
                totalItems: dataList.length,
                totalPages: 1,
                currentPage,
                limit: currentLimit,
                hasNextPage: false,
                hasPrevPage: false
            };

            setAffiliates(dataList);
            setPagination(paginationObj);
            setApiSummary(response?.summary || null);
        } catch (err) {
            console.error('Error fetching affiliate users:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load affiliate users from server.');
            setAffiliates([]);
        } finally {
            setLoading(false);
        }
    }, [limit]);

    // Initial and filter-triggered fetch
    useEffect(() => {
        fetchAffiliates(page, limit, debouncedSearch, statusFilter, payableFilter);
    }, [page, limit, debouncedSearch, statusFilter, payableFilter, fetchAffiliates]);

    // Debounce search input (400ms)
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Processed affiliates & summary metrics
    const processedAffiliates = affiliates.map(getAffiliateData).filter(Boolean);
    const totalPayableAll = apiSummary?.totalPayable ?? processedAffiliates.reduce((sum, aff) => sum + (aff.payableAmount || 0), 0);
    const totalAffiliatesCount = apiSummary?.totalAffiliates ?? (pagination.totalItems || affiliates.length);
    const activeCount = apiSummary?.activeCount ?? processedAffiliates.filter(a => a.status === 'active').length;

    const handleClearFilters = () => {
        setSearchTerm('');
        setDebouncedSearch('');
        setStatusFilter('');
        setPayableFilter('');
        setPage(1);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleViewAffiliateLinks = (affiliate) => {
        setSelectedAffiliateForLinks(affiliate);
        setCurrentView('links');
    };

    if (currentView === 'links') {
        return (
            <AffiliateLinksClicks
                selectedAffiliate={selectedAffiliateForLinks}
                onBack={() => {
                    setCurrentView('list');
                    setSelectedAffiliateForLinks(null);
                }}
            />
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Page Header */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1.5}
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography
                        sx={{
                            color: '#1E293B',
                            fontWeight: 600,
                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                            lineHeight: 1.2
                        }}
                    >
                        Affiliates
                    </Typography>
                    <Typography sx={{ color: '#64748B', fontSize: '0.82rem', mt: 0.5 }}>
                        {loading ? 'Loading affiliates...' : `${totalAffiliatesCount} affiliates · ${activeCount} active · Total Payable: ${formatCurrency(totalPayableAll)}`}
                    </Typography>
                </Box>

                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => fetchAffiliates(page, debouncedSearch, statusFilter, payableFilter)}
                    startIcon={<RefreshCw size={14} />}
                    disabled={loading}
                    sx={{
                        color: '#64748B',
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        height: '36px',
                        px: 1.5,
                        borderRadius: '6px',
                        border: '1px solid #E2E8F0',
                        bgcolor: '#FFFFFF',
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#F8FAFC',
                            borderColor: '#CBD5E1',
                            color: '#1E293B',
                            boxShadow: 'none'
                        }
                    }}
                >
                    Refresh
                </Button>
            </Stack>

            {/* Error Notification */}
            {error && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: '6px', fontSize: '0.82rem', boxShadow: 'none' }}>
                    {error}
                </Alert>
            )}

            {/* Search and Filters Bar */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    mb: 2.5,
                    borderRadius: '8px',
                    border: '1px solid #E8E8E8',
                    bgcolor: '#FFFFFF',
                    boxShadow: 'none'
                }}
            >
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 1.25 }}>
                    {/* Search Input */}
                    <TextField
                        placeholder="Search by affiliate name, email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                                    <Search size={16} color="#94A3B8" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flex: { xs: '1 1 100%', sm: '1 1 200px', md: '0 1 250px' },
                            bgcolor: '#FFFFFF',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                            },
                        }}
                    />

                    {/* Status Filter */}
                    <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 130 } }}>
                        <Select
                            displayEmpty
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            MenuProps={selectMenuProps}
                            renderValue={(selected) => (selected ? (STATUS_LABELS[selected] || selected) : <span style={{ color: '#64748B' }}>Status</span>)}
                            sx={{
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                color: '#1E293B',
                                bgcolor: '#FFFFFF',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="suspended">Suspended</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Payable Balance Filter */}
                    <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 155 } }}>
                        <Select
                            displayEmpty
                            value={payableFilter}
                            onChange={(e) => {
                                setPayableFilter(e.target.value);
                                setPage(1);
                            }}
                            MenuProps={selectMenuProps}
                            renderValue={(selected) => (selected ? (PAYABLE_LABELS[selected] || selected) : <span style={{ color: '#64748B' }}>Payable Balance</span>)}
                            sx={{
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                color: '#1E293B',
                                bgcolor: '#FFFFFF',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="">All Balances</MenuItem>
                            <MenuItem value="above2000">Payable now (≥ ₹2,000)</MenuItem>
                            <MenuItem value="below2000">Below threshold (&lt; ₹2,000)</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Clear Filters Button */}
                    {(searchTerm || statusFilter || payableFilter) && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleClearFilters}
                            startIcon={<RotateCcw size={14} />}
                            disableElevation
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            sx={{
                                color: '#64748B',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                height: '38px',
                                px: 1.5,
                                borderRadius: '6px',
                                border: '1px solid #E2E8F0',
                                '&:hover': {
                                    bgcolor: '#F8FAFC',
                                    borderColor: '#CBD5E1',
                                    color: '#1E293B'
                                }
                            }}
                        >
                            Reset
                        </Button>
                    )}
                </Stack>
            </Paper>

            {/* Affiliates Table */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '8px',
                    border: '1px solid #E8E8E8',
                    bgcolor: '#FFFFFF',
                    overflow: 'hidden',
                    boxShadow: 'none'
                }}
            >
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 960 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '50px' }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2, whiteSpace: 'nowrap', minWidth: '140px' }}>Affiliate</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2, whiteSpace: 'nowrap', minWidth: '170px' }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', minWidth: '110px' }}>Joined Date</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 1, whiteSpace: 'nowrap', textAlign: 'center', width: '70px' }}>Links</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 1, whiteSpace: 'nowrap', textAlign: 'center', width: '70px' }}>Clicks</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 1, whiteSpace: 'nowrap', textAlign: 'center', width: '80px' }}>Bookings</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', minWidth: '95px' }}>Pending</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', minWidth: '95px' }}>Payable</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', minWidth: '105px' }}>Lifetime Paid</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '90px' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2, whiteSpace: 'nowrap', textAlign: 'right', width: '80px' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : processedAffiliates.length > 0 ? (
                                processedAffiliates.map((aff, idx) => {
                                    const statusStyle = getStatusStyle(aff.status);
                                    return (
                                        <TableRow
                                            key={aff.id || idx}
                                            sx={{
                                                '& td': { borderBottom: '1px solid #F1F5F9' },
                                                '&:last-child td': { borderBottom: 0 },
                                            }}
                                        >
                                            {/* S.No */}
                                            <TableCell sx={{ textAlign: 'center', py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B' }}>
                                                {(page - 1) * limit + idx + 1}
                                            </TableCell>

                                            {/* Affiliate Name */}
                                            <TableCell sx={{ py: 1.25, px: 2 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.8rem',
                                                        fontWeight: 500,
                                                        color: '#1E293B',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                    title={aff.name}
                                                >
                                                    {aff.name}
                                                </Typography>
                                            </TableCell>

                                            {/* Email */}
                                            <TableCell sx={{ py: 1.25, px: 2 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.78rem',
                                                        color: '#475569',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                    title={aff.email}
                                                >
                                                    {aff.email}
                                                </Typography>
                                            </TableCell>

                                            {/* Joined Date */}
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(aff.joinedDate)}
                                            </TableCell>

                                            {/* Links */}
                                            <TableCell sx={{ py: 1.25, px: 1, textAlign: 'center', fontSize: '0.78rem', color: '#334155', fontWeight: 500 }}>
                                                {aff.links}
                                            </TableCell>

                                            {/* Clicks */}
                                            <TableCell sx={{ py: 1.25, px: 1, textAlign: 'center', fontSize: '0.78rem', color: '#334155', fontWeight: 500 }}>
                                                {aff.clicks.toLocaleString('en-IN')}
                                            </TableCell>

                                            {/* Bookings */}
                                            <TableCell sx={{ py: 1.25, px: 1, textAlign: 'center', fontSize: '0.78rem', color: '#334155', fontWeight: 500 }}>
                                                {aff.bookings}
                                            </TableCell>

                                            {/* Pending Amount */}
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.78rem', color: aff.pendingAmount > 0 ? '#B45309' : '#64748B', fontWeight: aff.pendingAmount > 0 ? 500 : 400, whiteSpace: 'nowrap' }}>
                                                {formatCurrency(aff.pendingAmount)}
                                            </TableCell>

                                            {/* Payable Amount */}
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.78rem', color: aff.payableAmount > 0 ? '#2E7D32' : '#64748B', fontWeight: aff.payableAmount > 0 ? 600 : 400, whiteSpace: 'nowrap' }}>
                                                {formatCurrency(aff.payableAmount)}
                                            </TableCell>

                                            {/* Lifetime Paid */}
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.78rem', color: '#334155', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                {formatCurrency(aff.lifetimePaid)}
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell sx={{ py: 1.25, px: 1.5, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                <Box
                                                    sx={{
                                                        px: 1.2,
                                                        py: 0.35,
                                                        borderRadius: '4px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500,
                                                        display: 'inline-block',
                                                        textTransform: 'lowercase',
                                                        bgcolor: statusStyle.bg,
                                                        color: statusStyle.color,
                                                        border: statusStyle.border
                                                    }}
                                                >
                                                    {aff.status}
                                                </Box>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="right" sx={{ py: 1.25, px: 2, whiteSpace: 'nowrap' }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disableElevation
                                                    disableFocusRipple
                                                    disableRipple
                                                    disableTouchRipple
                                                    onClick={() => handleViewAffiliateLinks(aff)}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500,
                                                        color: '#334155',
                                                        borderColor: '#CBD5E1',
                                                        borderRadius: '4px',
                                                        height: '28px',
                                                        py: 0,
                                                        px: 1.5,
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                            bgcolor: '#F8FAFC',
                                                            borderColor: '#94A3B8',
                                                            boxShadow: 'none'
                                                        }
                                                    }}
                                                >
                                                    View Links & Clicks
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.8rem' }}>
                                        No affiliate records found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Pagination */}
            <GlobalPagination
                page={page}
                pageSize={limit}
                totalRecords={pagination.totalItems || affiliates.length}
                totalPages={pagination.totalPages}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newLimit) => {
                    setLimit(newLimit);
                    setPage(1);
                }}
                loading={loading}
            />
        </Box>
    );
};

export default AffiliateInfo;